import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import maplibregl from 'maplibre-gl';
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronUp,
  LoaderCircle,
  LocateFixed,
  MapPin,
  Search,
  X,
} from 'lucide-react';
import { supabase } from '../services/supabaseClient';
import 'maplibre-gl/dist/maplibre-gl.css';

const MAP_STYLE_URL = 'https://tiles.openfreemap.org/styles/liberty';
const SEARCH_DEBOUNCE_MS = 360;
const REVERSE_DEBOUNCE_MS = 350;
const MAX_RESULTS = 7;
const SOUTH_AFRICA_CENTER = { latitude: -29, longitude: 24 };
const COLLAPSED_SHEET_HEIGHT = 108;
const GOOGLE_MAPS_VERSION = 'weekly';
const GOOGLE_PLACE_RESOLVE_TIMEOUT_MS = 12000;

const BROAD_TYPES = new Set([
  'country',
  'region',
  'province',
  'state',
  'county',
  'district',
  'place',
  'city',
  'town',
  'village',
  'locality',
  'neighborhood',
  'neighbourhood',
  'suburb',
  'municipality',
]);

const typography = {
  fontFamily: "'Manrope', 'Avenir Next', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
  fontKerning: 'normal',
};

const coordinate = (value) => Number(Number(value).toFixed(6));

const isCoordinate = (latitude, longitude) => {
  const lat = Number(latitude);
  const lng = Number(longitude);

  return (
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180
  );
};

const isInSouthAfrica = (point) => {
  if (!isCoordinate(point?.latitude, point?.longitude)) return false;

  const latitude = Number(point.latitude);
  const longitude = Number(point.longitude);

  return latitude >= -35.8 && latitude <= -21.4 && longitude >= 15.5 && longitude <= 33.8;
};

const distanceMetres = (from, to) => {
  if (!from || !to) return Number.POSITIVE_INFINITY;

  const toRadians = (value) => (Number(value) * Math.PI) / 180;
  const earthRadius = 6371000;
  const dLat = toRadians(Number(to.latitude) - Number(from.latitude));
  const dLng = toRadians(Number(to.longitude) - Number(from.longitude));

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(from.latitude)) *
      Math.cos(toRadians(to.latitude)) *
      Math.sin(dLng / 2) ** 2;

  return earthRadius * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const isWithinArea = (point, area) => {
  if (!area || !isCoordinate(point?.latitude, point?.longitude)) return false;

  const radius = Number(area.radius_metres ?? area.radiusMetres ?? 0);

  return Number.isFinite(radius) && radius > 0 && distanceMetres(area, point) <= radius;
};

const isSpecificLocation = (resultType, label) => {
  const type = String(resultType || '').trim().toLowerCase();
  const text = String(label || '').trim().toLowerCase();
  const approvedEndpoint = /airport|university|campus|residence|hotel|estate|mall/i.test(text);

  return (
    approvedEndpoint ||
    (!BROAD_TYPES.has(type) &&
      !['stellenbosch', 'cape town', 'western cape', 'south africa'].includes(text))
  );
};

const normaliseLocation = (value, fallbackSource = 'search') => {
  if (!value || !isCoordinate(value.latitude, value.longitude)) return null;

  const label = String(value.label || '').trim() || 'Selected location';
  const resultType = String(value.resultType || value.result_type || '').trim().toLowerCase();
  const rawSource = String(value.source || fallbackSource).trim().toLowerCase();
  const source =
    rawSource === 'current_location' || rawSource.includes('device')
      ? 'current_location'
      : rawSource === 'map_pin' || rawSource.includes('map') || rawSource.includes('confirmed')
        ? 'map_pin'
        : 'search';

  return {
    label,
    formattedAddress: String(
      value.formattedAddress ||
        value.formatted_address ||
        value.secondary_label ||
        value.label ||
        '',
    ).trim(),
    latitude: coordinate(value.latitude),
    longitude: coordinate(value.longitude),
    provider: String(value.provider || 'geoapify').trim().toLowerCase(),
    providerFeatureId: String(
      value.providerFeatureId ||
        value.provider_feature_id ||
        value.placeId ||
        value.place_id ||
        '',
    ).trim(),
    source,
    resultType,
    displayType: String(value.displayType || value.display_type || '').trim(),
    isSpecific:
      value.isSpecific !== false &&
      value.is_specific !== false &&
      isSpecificLocation(resultType, label),
    originalLabel: String(
      value.originalLabel || value.original_label || value.label || '',
    ).trim(),
  };
};

const normaliseSuggestion = (value) => {
  const providerFeatureId = String(
    value?.providerFeatureId ||
      value?.provider_feature_id ||
      value?.placeId ||
      value?.place_id ||
      value?.id ||
      '',
  ).trim();

  if (!providerFeatureId) return null;

  return {
    providerFeatureId,
    label: String(value?.label || '').trim() || 'Location result',
    formattedAddress: String(
      value?.formattedAddress || value?.formatted_address || value?.secondary_label || '',
    ).trim(),
    resultType: String(value?.resultType || value?.result_type || '').trim().toLowerCase(),
    displayType: String(value?.displayType || value?.display_type || '').trim(),
  };
};

const adjustedLabel = (location) => {
  const base = String(location?.originalLabel || location?.label || '').trim();
  if (!base) return 'Pinned location';
  return base.toLowerCase().includes('pin adjusted') ? base : `${base} — pin adjusted`;
};

const locationFunctionUrl = () => {
  const base = String(process.env.REACT_APP_SUPABASE_URL || supabase?.supabaseUrl || '').replace(/\/$/, '');
  return base ? `${base}/functions/v1/uniair-location-search` : '';
};

const invokeLocationSearch = async (body, { signal } = {}) => {
  const url = locationFunctionUrl();
  const anonKey = String(process.env.REACT_APP_SUPABASE_ANON_KEY || supabase?.supabaseKey || '');

  if (!url || !anonKey) {
    const { data, error } = await supabase.functions.invoke('uniair-location-search', { body });

    if (signal?.aborted) throw new DOMException('Request aborted', 'AbortError');
    if (error || !data?.ok) {
      throw new Error(
        data?.message ||
          'Location search is temporarily unavailable. You can still place the pin on the map.',
      );
    }

    return data;
  }

  const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) throw sessionError;

  const response = await fetch(url, {
    method: 'POST',
    signal,
    headers: {
      'Content-Type': 'application/json',
      apikey: anonKey,
      Authorization: `Bearer ${sessionData?.session?.access_token || anonKey}`,
    },
    body: JSON.stringify(body),
  });

  let data = null;
  try {
    data = await response.json();
  } catch {
    // A clear generic error is shown below when the function response is not JSON.
  }

  if (!response.ok || !data?.ok) {
    throw new Error(
      data?.message ||
        'Location search is temporarily unavailable. You can still place the pin on the map.',
    );
  }

  return data;
};


const installGoogleMapsImportLibrary = (options) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  // Google's supported Dynamic Library Import bootstrap, kept local to this
  // component so UniAir does not need to alter index.html on launch day.
  ((g) => {
    let h;
    let a;
    let k;
    const p = 'The Google Maps JavaScript API';
    const c = 'google';
    const l = 'importLibrary';
    const q = '__ib__';
    const m = document;
    const b = window;
    const googleRoot = (b[c] = b[c] || {});
    const mapsRoot = (googleRoot.maps = googleRoot.maps || {});
    const requestedLibraries = new Set();
    const params = new URLSearchParams();

    const load = () =>
      h ||
      (h = new Promise(async (resolve, reject) => {
        a = m.createElement('script');
        params.set('libraries', [...requestedLibraries].join(','));

        for (k in g) {
          params.set(
            k.replace(/[A-Z]/g, (value) => `_${value[0].toLowerCase()}`),
            g[k],
          );
        }

        params.set('callback', `${c}.maps.${q}`);
        a.src = `https://maps.${c}apis.com/maps/api/js?${params.toString()}`;
        mapsRoot[q] = resolve;
        a.onerror = () => {
          h = null;
          reject(new Error(`${p} could not load.`));
        };
        a.nonce = m.querySelector('script[nonce]')?.nonce || '';
        m.head.append(a);
      }));

    if (mapsRoot[l]) {
      // Another part of UniAir already installed the loader. Reuse it.
      return;
    }

    mapsRoot[l] = (libraryName, ...rest) => {
      requestedLibraries.add(libraryName);
      return load().then(() => mapsRoot[l](libraryName, ...rest));
    };
  })(options);
};

export const loadGooglePlacesUiKit = async () => {
  const apiKey = String(process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '').trim();

  if (!apiKey) {
    throw new Error('Google Places UI Kit key is not configured.');
  }

  if (!window.google?.maps?.importLibrary) {
    installGoogleMapsImportLibrary({
      key: apiKey,
      v: GOOGLE_MAPS_VERSION,
    });
  }

  return window.google.maps.importLibrary('places');
};

const googleLatLngToPoint = (location) => {
  if (!location) return null;

  const latitude =
    typeof location.lat === 'function'
      ? location.lat()
      : location.lat ?? location.latitude;

  const longitude =
    typeof location.lng === 'function'
      ? location.lng()
      : location.lng ?? location.longitude;

  if (!isCoordinate(latitude, longitude)) return null;

  return {
    latitude: coordinate(latitude),
    longitude: coordinate(longitude),
  };
};

const GENERIC_GOOGLE_LABELS = new Set([
  'selected place',
  'selected location',
  'place',
  'location result',
]);

const cleanGoogleLabel = (value) => {
  const text = String(value || '').trim();
  if (!text) return '';
  return GENERIC_GOOGLE_LABELS.has(text.toLowerCase()) ? '' : text;
};

const readableType = (suggestion) => {
  const explicit = String(suggestion?.displayType || '').trim();
  if (explicit) return explicit;

  const type = String(suggestion?.resultType || '').toLowerCase();
  const title = String(suggestion?.label || '').toLowerCase();

  if (title.includes('airport')) return 'Airport';
  if (title.includes('university') || title.includes('campus')) return 'Campus';
  if (title.includes('residence') || title.includes('student')) return 'Residence';
  if (['address', 'building', 'house'].includes(type)) return 'Address';
  if (['amenity', 'commercial', 'accommodation', 'tourism', 'poi'].includes(type)) return 'Landmark';
  if (type === 'street') return 'Street';

  return 'Place';
};

/**
 * One stable, portal-mounted picker for Airport Booking and Private Rides.
 *
 * Deliberately avoided here:
 * - CSS height/top transitions over the WebGL canvas
 * - backdrop filters over MapLibre
 * - ResizeObserver-driven sheet sizing
 * - map resize calls caused by typing or search-result changes
 *
 * Those four things created the repeated pins, drawer ghosts and black map bands.
 */
export default function UniAirLocationPicker({
  brandLabel = 'UniAir',
  title = 'Set location',
  searchHint = 'Search your address, residence, gate or landmark',
  value,
  areaMode = 'market', // market | south_africa
  searchScope = 'market',
  onClose,
  onConfirm,
  onClear,
}) {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const inputRef = useRef(null);
  const searchSurfaceRef = useRef(null);
  const googleAutocompleteElementRef = useRef(null);
  const googleDetailsElementRef = useRef(null);
  const googleDetailsRequestRef = useRef(null);
  const googlePendingPlaceIdRef = useRef('');
  const googlePendingSearchLabelRef = useRef('');
  const googleLastTypedSearchRef = useRef('');
  const googleResolveTimerRef = useRef(null);
  const searchTimerRef = useRef(null);
  const reverseTimerRef = useRef(null);
  const searchAbortRef = useRef(null);
  const retrieveAbortRef = useRef(null);
  const reverseAbortRef = useRef(null);
  const searchSequenceRef = useRef(0);
  const retrieveSequenceRef = useRef(0);
  const reverseSequenceRef = useRef(0);
  const latestLocationRef = useRef(normaliseLocation(value));
  const initialLocationRef = useRef(normaliseLocation(value));
  const sheetHeightRef = useRef(COLLAPSED_SHEET_HEIGHT);

  const [searchArea, setSearchArea] = useState(null);
  const [contextLoading, setContextLoading] = useState(true);
  const [contextReady, setContextReady] = useState(false);
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [googleSearchStatus, setGoogleSearchStatus] = useState('loading');
  const [googleSearchMessage, setGoogleSearchMessage] = useState('');
  const [searching, setSearching] = useState(false);
  const [retrievingId, setRetrievingId] = useState('');
  const [reverseLoading, setReverseLoading] = useState(false);
  const [locating, setLocating] = useState(false);
  const [draftLocation, setDraftLocation] = useState(() => normaliseLocation(value));
  const [error, setError] = useState('');
  const [mapError, setMapError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [mapReady, setMapReady] = useState(false);
  const [sheetExpanded, setSheetExpanded] = useState(true);
  const [viewportHeight, setViewportHeight] = useState(
    () => window.visualViewport?.height || window.innerHeight || 720,
  );
  const [searchMode, setSearchMode] = useState(false);

  const skipNextSearchRef = useRef(false);
  const draft = useMemo(() => normaliseLocation(draftLocation), [draftLocation]);

  const expandedSheetHeight = useMemo(() => {
    const viewport = Math.max(560, Number(viewportHeight) || 720);

    // Keep one stable expanded height for the entire picker session. Search
    // results, reverse geocoding and place resolution are asynchronous; when
    // those states used to change the sheet from ~48% to ~72% viewport height
    // they produced the exact mobile CLS selector reported by Speed Insights.
    return Math.min(620, Math.max(430, Math.round(viewport * 0.68)));
  }, [viewportHeight]);

  const activeSheetHeight = sheetExpanded
    ? expandedSheetHeight
    : COLLAPSED_SHEET_HEIGHT;
  const markerTipY = Math.max(132, Math.round((viewportHeight - activeSheetHeight) / 2));
  const controlHeight = Math.max(182, activeSheetHeight - 104);

  const canUsePoint = (point) =>
    areaMode === 'south_africa'
      ? isInSouthAfrica(point)
      : isWithinArea(point, searchArea);

  const areaLabel =
    areaMode === 'south_africa'
      ? 'South Africa'
      : searchArea?.label || 'UniAir booking area';

  const outsideAreaMessage = () =>
    areaMode === 'south_africa'
      ? 'Choose a destination in South Africa.'
      : `Please place the pin inside the ${searchArea?.label || 'selected UniAir'} booking area.`;

  const canConfirm =
    !!draft &&
    draft.isSpecific === true &&
    !reverseLoading &&
    canUsePoint(draft);

  const cancelSearch = () => {
    searchSequenceRef.current += 1;
    searchAbortRef.current?.abort();
    if (searchTimerRef.current) window.clearTimeout(searchTimerRef.current);
    searchTimerRef.current = null;
    setSearching(false);
  };

  const cancelRetrieve = () => {
    retrieveSequenceRef.current += 1;
    retrieveAbortRef.current?.abort();
    setRetrievingId('');
  };

  const cancelReverse = () => {
    reverseSequenceRef.current += 1;
    reverseAbortRef.current?.abort();
    if (reverseTimerRef.current) window.clearTimeout(reverseTimerRef.current);
    reverseTimerRef.current = null;
    setReverseLoading(false);
  };

  const locationPayload = (body) => ({
    ...body,
    search_scope: searchScope,
    searchScope,
    scope: searchScope,
  });

  const setDraft = (next) => {
    const normalised = normaliseLocation(next);
    if (!normalised) return null;

    latestLocationRef.current = normalised;
    setDraftLocation(normalised);
    return normalised;
  };

  const currentMapPadding = () => ({
    top: 0,
    right: 0,
    bottom: Math.max(0, Number(sheetHeightRef.current) || 0),
    left: 0,
  });

  const applyMapPadding = () => {
    const map = mapRef.current;
    if (!map || !map.loaded()) return;
    map.setPadding(currentMapPadding());
  };

  const moveMapTo = (point, zoom = 17.1) => {
    const map = mapRef.current;
    if (!map || !isCoordinate(point?.latitude, point?.longitude)) return;

    map.stop();
    map.easeTo({
      center: [Number(point.longitude), Number(point.latitude)],
      zoom: Math.max(16.7, Number(zoom) || 17.1),
      padding: currentMapPadding(),
      duration: 420,
      essential: true,
    });
  };

  const reversePoint = (point, { source = 'map_pin', preserveVenueName = true } = {}) => {
    if (!isCoordinate(point?.latitude, point?.longitude)) return;
    if (!canUsePoint(point)) {
      setError(outsideAreaMessage());
      return;
    }

    cancelReverse();

    const sequence = ++reverseSequenceRef.current;
    const controller = new AbortController();
    reverseAbortRef.current = controller;
    setReverseLoading(true);

    const previous = latestLocationRef.current;
    const pendingLabel =
      source === 'map_pin' && preserveVenueName
        ? adjustedLabel(previous)
        : previous?.label || 'Pinned location';

    setDraft({
      ...previous,
      label: pendingLabel,
      formattedAddress: previous?.formattedAddress || '',
      latitude: point.latitude,
      longitude: point.longitude,
      source,
      isSpecific: true,
      originalLabel:
        source === 'map_pin' && preserveVenueName
          ? previous?.originalLabel || previous?.label || ''
          : previous?.originalLabel || '',
    });

    if (source === 'map_pin') {
      setStatusMessage('Pin adjusted — pickup location updated.');
    }

    reverseTimerRef.current = window.setTimeout(async () => {
      try {
        const data = await invokeLocationSearch(
          locationPayload({
            action: 'reverse',
            latitude: Number(point.latitude),
            longitude: Number(point.longitude),
          }),
          { signal: controller.signal },
        );

        if (sequence !== reverseSequenceRef.current) return;

        const providerLocation = data?.location || data?.result || {};
        const resolved = normaliseLocation({
          ...providerLocation,
          latitude: point.latitude,
          longitude: point.longitude,
          label:
            source === 'map_pin' && preserveVenueName
              ? adjustedLabel(previous)
              : providerLocation?.label || previous?.label || 'Pinned location',
          source,
          isSpecific: true,
          originalLabel:
            source === 'map_pin' && preserveVenueName
              ? previous?.originalLabel || previous?.label || ''
              : providerLocation?.label || previous?.originalLabel || '',
        });

        setDraft(
          resolved || {
            ...previous,
            label: pendingLabel,
            latitude: point.latitude,
            longitude: point.longitude,
            source,
            isSpecific: true,
          },
        );
      } catch (reverseError) {
        if (reverseError?.name === 'AbortError' || sequence !== reverseSequenceRef.current) return;

        setDraft({
          ...previous,
          label: pendingLabel,
          latitude: point.latitude,
          longitude: point.longitude,
          source,
          isSpecific: true,
        });

        setStatusMessage(
          source === 'map_pin'
            ? 'Pin adjusted — the address lookup could not finish.'
            : 'Your coordinates were found, but the address lookup could not finish.',
        );
      } finally {
        if (sequence === reverseSequenceRef.current) setReverseLoading(false);
      }
    }, REVERSE_DEBOUNCE_MS);
  };

  const handleManualDragEnd = () => {
    const map = mapRef.current;
    if (!map) return;

    const centre = map.getCenter();
    const point = {
      latitude: coordinate(centre.lat),
      longitude: coordinate(centre.lng),
    };

    if (!canUsePoint(point)) {
      setError(outsideAreaMessage());
      return;
    }

    setError('');
    setSuggestionsOpen(false);
    reversePoint(point, { source: 'map_pin', preserveVenueName: true });
  };

  const toggleSheet = () => {
    setSuggestionsOpen(false);
    setSearchMode(false);
    setSheetExpanded((expanded) => !expanded);
  };

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const previousOverscroll = document.body.style.overscrollBehavior;

    document.body.style.overflow = 'hidden';
    document.body.style.overscrollBehavior = 'none';

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.overscrollBehavior = previousOverscroll;
      cancelSearch();
      cancelRetrieve();
      cancelReverse();
      if (googleResolveTimerRef.current) {
        window.clearTimeout(googleResolveTimerRef.current);
        googleResolveTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let resizeTimer = null;

    const updateViewport = () => {
      const nextHeight = window.visualViewport?.height || window.innerHeight || 720;
      setViewportHeight(nextHeight);

      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        const map = mapRef.current;
        if (!map) return;
        map.resize();
        applyMapPadding();
      }, 80);
    };

    updateViewport();
    window.addEventListener('resize', updateViewport);
    window.visualViewport?.addEventListener('resize', updateViewport);

    return () => {
      window.clearTimeout(resizeTimer);
      window.removeEventListener('resize', updateViewport);
      window.visualViewport?.removeEventListener('resize', updateViewport);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    const loadContext = async () => {
      try {
        setContextLoading(true);

        const data = await invokeLocationSearch(
          locationPayload({ action: 'context' }),
          { signal: controller.signal },
        );
        const area =
          data?.search_area ||
          data?.context?.serviceArea ||
          data?.context?.service_area ||
          null;

        if (area && isCoordinate(area.latitude, area.longitude)) {
          if (active) setSearchArea(area);
        } else if (areaMode === 'market') {
          throw new Error('UniAir could not load your booking area. Please go back and try again.');
        }
      } catch (contextError) {
        if (!active || contextError?.name === 'AbortError') return;
        if (areaMode === 'market') {
          setError(
            contextError?.message ||
              'Could not load the UniAir booking area. Please go back and try again.',
          );
        }
      } finally {
        if (active) {
          setContextLoading(false);
          setContextReady(true);
        }
      }
    };

    void loadContext();

    return () => {
      active = false;
      controller.abort();
    };
  }, [areaMode, searchScope]);


  // Load Places UI Kit once the UniAir market/search context is ready.
  // Important: Google custom elements are rendered declaratively by React below.
  // We do not append/remove them manually; mixing React DOM ownership with
  // Google-owned elements is what caused the production removeChild crash.
  useEffect(() => {
    if (!contextReady) return undefined;

    let active = true;
    let timeoutId = null;

    const initialiseGooglePlacesUiKit = async () => {
      const apiKey = String(
        process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
      ).trim();

      if (!apiKey) {
        if (active) {
          setGoogleSearchStatus('fallback');
          setGoogleSearchMessage(
            'Google Places is not configured yet. UniAir is using the existing location search.',
          );
        }
        return;
      }

      try {
        setGoogleSearchStatus('loading');
        setGoogleSearchMessage('');

        await loadGooglePlacesUiKit();

        const definitionsReady = Promise.all([
          window.customElements.whenDefined('gmp-basic-place-autocomplete'),
          window.customElements.whenDefined('gmp-place-details-compact'),
          window.customElements.whenDefined('gmp-place-details-place-request'),
          window.customElements.whenDefined('gmp-place-standard-content'),
        ]);

        const definitionTimeout = new Promise((_, reject) => {
          timeoutId = window.setTimeout(() => {
            reject(new Error('Google Places UI Kit elements did not become ready in time.'));
          }, GOOGLE_PLACE_RESOLVE_TIMEOUT_MS);
        });

        await Promise.race([definitionsReady, definitionTimeout]);

        if (timeoutId) {
          window.clearTimeout(timeoutId);
          timeoutId = null;
        }

        if (!active) return;

        setGoogleSearchStatus('ready');
        setGoogleSearchMessage('');
      } catch (googleError) {
        console.error('Google Places UI Kit load error:', googleError);

        if (active) {
          setGoogleSearchStatus('fallback');
          setGoogleSearchMessage(
            'Google Places could not load. UniAir has automatically switched to the existing location search.',
          );
        }
      }
    };

    void initialiseGooglePlacesUiKit();

    return () => {
      active = false;
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [contextReady]);

  // Configure and listen to the React-owned Google custom elements.
  useEffect(() => {
    if (googleSearchStatus !== 'ready' || !sheetExpanded) return undefined;

    const autocompleteElement = googleAutocompleteElementRef.current;
    const detailsElement = googleDetailsElementRef.current;
    const detailsRequest = googleDetailsRequestRef.current;

    if (!autocompleteElement || !detailsElement || !detailsRequest) {
      return undefined;
    }

    const locationBias =
      searchArea && isCoordinate(searchArea.latitude, searchArea.longitude)
        ? {
            radius: Math.max(
              5000,
              Math.min(
                50000,
                Number(searchArea.radius_metres ?? searchArea.radiusMetres ?? 40000),
              ),
            ),
            center: {
              lat: Number(searchArea.latitude),
              lng: Number(searchArea.longitude),
            },
          }
        : null;

    // These are element properties (not string HTML attributes).
    autocompleteElement.includedRegionCodes = ['za'];
    autocompleteElement.locationBias = locationBias;
    autocompleteElement.setAttribute(
      'aria-label',
      searchHint || 'Search for a place',
    );

    const readGoogleAutocompleteText = (event) => {
      const candidates = [];

      if (typeof event?.composedPath === 'function') {
        for (const node of event.composedPath()) {
          if (node && typeof node.value === 'string') candidates.push(node.value);
        }
      }

      if (typeof autocompleteElement?.value === 'string') {
        candidates.push(autocompleteElement.value);
      }

      // Basic Place Autocomplete clears itself after selection. While the user
      // is typing, keep a copy of the internal input value when Google exposes
      // an open shadow root. This is only a fallback; the selected Google name
      // still comes from Place.fetchFields() above.
      const shadowInput = autocompleteElement?.shadowRoot?.querySelector?.('input');
      if (shadowInput && typeof shadowInput.value === 'string') {
        candidates.push(shadowInput.value);
      }

      return String(candidates.find((value) => String(value || '').trim()) || '').trim();
    };

    const handleGoogleInput = (event) => {
      // Basic Place Autocomplete clears its field after a selection and only
      // exposes the selected Place ID. Remember the user's non-empty search text
      // so a reverse geocoder can never replace it with an unrelated suburb name.
      const text = readGoogleAutocompleteText(event);
      if (text) googleLastTypedSearchRef.current = text;
    };

    const handleSearchFocus = () => {
      setSheetExpanded(true);
      setSearchMode(true);
    };

    const handlePlaceLoaded = async () => {
      const pendingPlaceId = googlePendingPlaceIdRef.current;
      if (!pendingPlaceId) return;

      // IMPORTANT: Places UI Kit deliberately exposes only place ID, location
      // and viewport programmatically. The name shown inside Google's details
      // card is NOT exposed by the UI Kit `place` object. To keep the booking
      // label identical to the Google result the passenger selected, create a
      // real Places API Place from the selected ID and fetch the exact fields.
      // The UI Kit place remains our coordinate fallback.
      const uiKitPlace = detailsElement.place;
      let place = null;

      try {
        const Place = window.google?.maps?.places?.Place;
        if (typeof Place === 'function') {
          place = new Place({ id: pendingPlaceId });
          await place.fetchFields({
            fields: ['displayName', 'formattedAddress', 'location', 'primaryType'],
          });
        }
      } catch (fieldError) {
        // Do not reverse-geocode a Google selection into a different suburb or
        // nearby street. If Places API (New) is unavailable, retain the exact
        // text the passenger typed as the safe fallback.
        console.warn('Could not fetch exact Google Place fields:', fieldError);
        place = null;
      }

      const point = googleLatLngToPoint(place?.location || uiKitPlace?.location);

      if (!point) {
        setRetrievingId('');
        googlePendingPlaceIdRef.current = '';
        googlePendingSearchLabelRef.current = '';
        setError(
          'Google found that place, but UniAir could not read its exact map point. Try the result again or place the pin.',
        );
        return;
      }

      const googleDisplayName = cleanGoogleLabel(place?.displayName);
      const typedSelectionLabel = cleanGoogleLabel(googlePendingSearchLabelRef.current);
      const selectedFormattedAddress = String(
        place?.formattedAddress ||
          place?.shortFormattedAddress ||
          '',
      ).trim();
      const selectedSearchLabel =
        googleDisplayName ||
        typedSelectionLabel ||
        cleanGoogleLabel(selectedFormattedAddress);

      // Never substitute a reverse-geocoded Geoapify label for a selected
      // Google place. That was the cause of MySpace at Dorpstreet becoming
      // "Inanda" and 89 Bree St becoming a nearby Hout Street address.
      if (!selectedSearchLabel) {
        setRetrievingId('');
        googlePendingPlaceIdRef.current = '';
        googlePendingSearchLabelRef.current = '';
        setError(
          'UniAir could not read the selected Google place name. Please select the result again.',
        );
        return;
      }

      const next = normaliseLocation({
        label: selectedSearchLabel,
        formattedAddress: selectedFormattedAddress || selectedSearchLabel,
        latitude: point.latitude,
        longitude: point.longitude,
        provider: 'google',
        providerFeatureId: pendingPlaceId,
        source: 'search',
        resultType: String(place?.primaryType || 'poi').trim().toLowerCase(),
        isSpecific: true,
        originalLabel: selectedSearchLabel,
      });

      if (!next || !canUsePoint(next)) {
        setRetrievingId('');
        googlePendingPlaceIdRef.current = '';
        googlePendingSearchLabelRef.current = '';
        setError(outsideAreaMessage());
        return;
      }

      if (googlePendingPlaceIdRef.current !== pendingPlaceId) return;

      setDraft(next);
      moveMapTo(next);
      setSuggestions([]);
      setSuggestionsOpen(false);
      setError('');
      setStatusMessage(
        next.label && next.label !== 'Selected location'
          ? 'Google place selected. You do not need to move the pin unless the entrance is elsewhere.'
          : 'Google place selected. The exact map point is saved; move the pin only if the entrance is elsewhere.',
      );
      setSearchMode(false);

      googlePendingPlaceIdRef.current = '';
      googlePendingSearchLabelRef.current = '';
      setRetrievingId('');

      if (googleResolveTimerRef.current) {
        window.clearTimeout(googleResolveTimerRef.current);
        googleResolveTimerRef.current = null;
      }
    };

    const handleGoogleSelection = (event) => {
      const placeId = String(event?.place?.id || '').trim();
      if (!placeId) return;

      cancelSearch();
      cancelRetrieve();
      cancelReverse();

      googlePendingPlaceIdRef.current = placeId;
      googlePendingSearchLabelRef.current =
        readGoogleAutocompleteText(event) || googleLastTypedSearchRef.current;
      setRetrievingId(placeId);
      setError('');
      setStatusMessage('Confirming the selected Google place…');

      if (googleResolveTimerRef.current) {
        window.clearTimeout(googleResolveTimerRef.current);
      }

      googleResolveTimerRef.current = window.setTimeout(() => {
        if (googlePendingPlaceIdRef.current !== placeId) return;

        googlePendingPlaceIdRef.current = '';
        googlePendingSearchLabelRef.current = '';
        setRetrievingId('');
        setError(
          'Google is taking too long to confirm that place. Try selecting it again, or use the map pin.',
        );
      }, GOOGLE_PLACE_RESOLVE_TIMEOUT_MS);

      // Google documents this as the supported bridge from Basic Autocomplete
      // to Places UI Kit Details.
      detailsRequest.place = placeId;
    };

    // Capture the Basic Autocomplete text while the passenger types. Google
    // clears the input before/when the selection event fires, so keeping this
    // live copy gives us a safe fallback if the Places API details request ever
    // fails. The exact selected Google displayName is still preferred.
    let shadowInput = null;
    let shadowObserver = null;

    const bindShadowInput = () => {
      const nextInput = autocompleteElement?.shadowRoot?.querySelector?.('input') || null;
      if (nextInput === shadowInput) return;

      if (shadowInput) {
        shadowInput.removeEventListener('input', handleGoogleInput);
        shadowInput.removeEventListener('change', handleGoogleInput);
      }

      shadowInput = nextInput;
      if (shadowInput) {
        shadowInput.addEventListener('input', handleGoogleInput);
        shadowInput.addEventListener('change', handleGoogleInput);
      }
    };

    bindShadowInput();
    if (autocompleteElement?.shadowRoot && typeof MutationObserver !== 'undefined') {
      shadowObserver = new MutationObserver(bindShadowInput);
      shadowObserver.observe(autocompleteElement.shadowRoot, { childList: true, subtree: true });
    }

    detailsElement.addEventListener('gmp-load', handlePlaceLoaded);
    autocompleteElement.addEventListener('gmp-select', handleGoogleSelection);
    autocompleteElement.addEventListener('input', handleGoogleInput);
    autocompleteElement.addEventListener('change', handleGoogleInput);
    autocompleteElement.addEventListener('keyup', handleGoogleInput);
    autocompleteElement.addEventListener('focusin', handleSearchFocus);

    return () => {
      detailsElement.removeEventListener('gmp-load', handlePlaceLoaded);
      autocompleteElement.removeEventListener('gmp-select', handleGoogleSelection);
      autocompleteElement.removeEventListener('input', handleGoogleInput);
      autocompleteElement.removeEventListener('change', handleGoogleInput);
      autocompleteElement.removeEventListener('keyup', handleGoogleInput);
      autocompleteElement.removeEventListener('focusin', handleSearchFocus);
      shadowObserver?.disconnect();
      if (shadowInput) {
        shadowInput.removeEventListener('input', handleGoogleInput);
        shadowInput.removeEventListener('change', handleGoogleInput);
      }
    };
    // The search context is stable for one picker session; rerun only if the
    // element is remounted after collapsing/reopening the sheet.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleSearchStatus, sheetExpanded, searchArea]);


  useEffect(() => {
    const next = normaliseLocation(value);
    latestLocationRef.current = next;
    setDraftLocation(next);
  }, [value?.latitude, value?.longitude, value?.label, value?.formattedAddress, value?.source]);

  useEffect(() => {
    if (!contextReady || !mapContainerRef.current || mapRef.current) return undefined;
    if (areaMode === 'market' && !searchArea) return undefined;

    const start = initialLocationRef.current || searchArea || SOUTH_AFRICA_CENTER;
    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      style: MAP_STYLE_URL,
      center: [Number(start.longitude), Number(start.latitude)],
      zoom: initialLocationRef.current ? 16.5 : areaMode === 'market' ? 14.2 : 5.2,
      attributionControl: true,
      interactive: true,
      dragPan: true,
      scrollZoom: true,
      doubleClickZoom: true,
      touchZoomRotate: true,
      renderWorldCopies: false,
    });

    map.dragPan.enable();
    map.scrollZoom.enable();
    map.doubleClickZoom.enable();
    map.touchZoomRotate.enable();
    map.touchZoomRotate.disableRotation();
    map.dragRotate.disable();
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');

    map.on('load', () => {
      map.getCanvasContainer().style.touchAction = 'none';
      map.getCanvas().style.touchAction = 'none';
      map.getCanvas().style.background = '#e8e7e2';
      map.resize();
      map.setPadding(currentMapPadding());
      setMapReady(true);
    });

    map.on('dragend', handleManualDragEnd);
    map.on('error', () => {
      setMapError(
        'The map could not load completely. Search for the address or try again before confirming the pin.',
      );
    });

    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
      setMapReady(false);
    };
    // The map must mount once only. Re-creating it after sheet/search state changes
    // caused the previous stacked pins and black canvas artefacts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextReady, areaMode]);

  useEffect(() => {
    sheetHeightRef.current = activeSheetHeight;
    if (mapReady) applyMapPadding();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSheetHeight, mapReady]);

  useEffect(() => {
    if (skipNextSearchRef.current) {
      skipNextSearchRef.current = false;
      cancelSearch();
      setSuggestions([]);
      setSuggestionsOpen(false);
      return undefined;
    }

    cancelSearch();

    const input = String(query || '').trim();
    const waitingForMarketContext = areaMode === 'market' && (contextLoading || !searchArea);

    if (waitingForMarketContext || input.length < 3) {
      setSuggestions([]);
      setSuggestionsOpen(false);
      return undefined;
    }

    const sequence = ++searchSequenceRef.current;

    searchTimerRef.current = window.setTimeout(async () => {
      const controller = new AbortController();
      searchAbortRef.current = controller;

      try {
        setSearching(true);

        const data = await invokeLocationSearch(
          locationPayload({ action: 'suggest', input, limit: MAX_RESULTS }),
          { signal: controller.signal },
        );

        if (sequence !== searchSequenceRef.current) return;

        const unique = Array.from(
          new Map(
            (Array.isArray(data?.suggestions) ? data.suggestions : [])
              .map(normaliseSuggestion)
              .filter(Boolean)
              .slice(0, MAX_RESULTS)
              .map((item) => [item.providerFeatureId, item]),
          ).values(),
        );

        setSuggestions(unique);
        setSuggestionsOpen(true);
      } catch (searchError) {
        if (searchError?.name === 'AbortError' || sequence !== searchSequenceRef.current) return;
        setSuggestions([]);
        setSuggestionsOpen(true);
        setError(
          searchError?.message ||
            'Could not search for that location. You can still place the pin on the map.',
        );
      } finally {
        if (sequence === searchSequenceRef.current) setSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (searchTimerRef.current) window.clearTimeout(searchTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, areaMode, contextLoading, searchArea, searchScope]);

  useEffect(() => {
    const closeOnOutsidePress = (event) => {
      if (!searchSurfaceRef.current?.contains(event.target)) setSuggestionsOpen(false);
    };

    document.addEventListener('pointerdown', closeOnOutsidePress);

    return () => document.removeEventListener('pointerdown', closeOnOutsidePress);
  }, []);

  const chooseSuggestion = async (suggestion) => {
    const selected = normaliseSuggestion(suggestion);
    if (!selected || retrievingId) return;

    cancelSearch();
    cancelRetrieve();
    cancelReverse();

    const sequence = ++retrieveSequenceRef.current;
    const controller = new AbortController();
    retrieveAbortRef.current = controller;

    setRetrievingId(selected.providerFeatureId);
    setError('');

    try {
      const data = await invokeLocationSearch(
        locationPayload({
          action: 'retrieve',
          place_id: selected.providerFeatureId,
          fallback_label: selected.label,
          fallback_formatted_address: selected.formattedAddress,
          fallback_result_type: selected.resultType,
        }),
        { signal: controller.signal },
      );

      if (sequence !== retrieveSequenceRef.current) return;

      const next = normaliseLocation({
        ...(data?.location || data?.result || {}),
        source: 'search',
        originalLabel: data?.location?.label || data?.result?.label || selected.label,
      });

      if (!next) {
        throw new Error('UniAir could not confirm that location. Try another result or place the pin.');
      }

      if (!canUsePoint(next)) {
        setError(outsideAreaMessage());
        return;
      }

      setDraft(next);
      moveMapTo(next);

      const nextQuery = String(next.label || '');
      skipNextSearchRef.current = String(query || '') !== nextQuery;
      setQuery(nextQuery);
      setSuggestions([]);
      setSuggestionsOpen(false);
      setStatusMessage(
        next.isSpecific
          ? 'Location selected. You do not need to move the pin unless the entrance is elsewhere.'
          : 'Please move the pin to a specific building, entrance, or pickup point.',
      );
      setSearchMode(false);
    } catch (retrieveError) {
      if (retrieveError?.name === 'AbortError' || sequence !== retrieveSequenceRef.current) return;
      setError(
        retrieveError?.message ||
          'Could not confirm that search result. Your previous location has not been changed.',
      );
    } finally {
      if (sequence === retrieveSequenceRef.current) setRetrievingId('');
    }
  };

  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError('This browser does not support location. Search for the address or place the pin on the map.');
      return;
    }

    if (areaMode === 'market' && (contextLoading || !searchArea)) {
      setError('UniAir is still loading your booking area. Please try again in a moment.');
      return;
    }

    setLocating(true);
    setError('');

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocating(false);

        const point = {
          latitude: coordinate(position.coords.latitude),
          longitude: coordinate(position.coords.longitude),
        };

        if (!canUsePoint(point)) {
          setError(outsideAreaMessage());
          return;
        }

        moveMapTo(point);
        reversePoint(point, {
          source: 'current_location',
          preserveVenueName: false,
        });
      },
      () => {
        setLocating(false);
        setError('UniAir could not access your location. Allow location access or search for the address.');
      },
      {
        enableHighAccuracy: true,
        maximumAge: 30000,
        timeout: 15000,
      },
    );
  };

  const clearSearch = () => {
    setQuery('');
    setSuggestions([]);
    setSuggestionsOpen(false);
    setError('');
    setStatusMessage('');
    inputRef.current?.focus();
  };

  const confirm = () => {
    if (!draft || !draft.isSpecific) {
      setError('Please choose a specific address, residence, building, or pin location.');
      return;
    }

    if (!canUsePoint(draft)) {
      setError(outsideAreaMessage());
      return;
    }

    onConfirm?.(draft);
  };

  if (typeof document === 'undefined') return null;

  const picker = (
    <div
      className="fixed left-0 top-0 z-[99999] w-screen overflow-hidden bg-[#e8e7e2]"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      style={{ ...typography, height: `${viewportHeight}px` }}
    >
      <div
        ref={mapContainerRef}
        className="absolute inset-0 overflow-hidden bg-[#e8e7e2]"
        style={{ touchAction: 'none' }}
      />

      <header className="absolute left-4 right-4 top-[max(0.75rem,env(safe-area-inset-top))] z-30 flex items-center gap-3">
        <button
          type="button"
          onClick={onClose}
          className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white text-[#720e1e] shadow-lg ring-1 ring-black/10 transition hover:bg-gray-50"
          aria-label="Back to booking"
        >
          <ChevronLeft className="h-6 w-6" strokeWidth={2.3} />
        </button>

        <div className="min-w-0 rounded-2xl bg-[#151515] px-3 py-2 text-white shadow-lg">
          <p className="text-[10px] font-bold uppercase tracking-[0.10em] text-white/65">
            {brandLabel}
          </p>
          <h2 className="truncate text-base font-bold">{title}</h2>
        </div>
      </header>

      <div
        className="pointer-events-none absolute left-1/2 z-20 -translate-x-1/2 -translate-y-full text-[#720e1e]"
        style={{ top: `${markerTipY}px` }}
      >
        <MapPin className="h-12 w-12 fill-[#720e1e] text-white" strokeWidth={1.8} />
      </div>

      <div
        className="pointer-events-none absolute left-1/2 z-20 mt-2 -translate-x-1/2 whitespace-nowrap rounded-full bg-[#151515] px-3 py-1.5 text-[11px] font-bold text-white shadow"
        style={{ top: `${markerTipY + 10}px` }}
      >
        {draft
          ? 'Pin is shown above — drag the map only to adjust the entrance'
          : 'Search or drag the map to set the pin'}
      </div>

      <section
        className="absolute inset-x-0 bottom-0 z-30 overflow-hidden rounded-t-[2rem] bg-white shadow-[0_-10px_24px_rgba(0,0,0,0.22)]"
        style={{ height: `${activeSheetHeight}px` }}
      >
        <div className="mx-auto max-w-xl">
          <button
            type="button"
            onClick={toggleSheet}
            className="block w-full px-4 pb-3 pt-3 text-left"
            aria-expanded={sheetExpanded}
            aria-controls="uniair-location-picker-controls"
          >
            <span className="mx-auto block h-1.5 w-12 rounded-full bg-gray-300" />
            <span className="mt-3 flex items-start gap-3">
              <span className="min-w-0 flex-1">
                <span className="block text-lg font-bold text-gray-900">
                  Choose your exact location
                </span>
                <span className="mt-1 block text-xs leading-5 text-gray-600">
                  {sheetExpanded
                    ? 'Tap here to hide the controls and view more of the map.'
                    : draft
                      ? 'Location selected. Tap here to view or change it.'
                      : 'Tap here to search or set the pin.'}
                </span>
              </span>

              <span className="mt-0.5 inline-flex shrink-0 items-center gap-1 rounded-full bg-[#720e1e]/8 px-2.5 py-1 text-[10px] font-bold text-[#720e1e]">
                {sheetExpanded ? 'Hide' : 'Open'}
                {sheetExpanded ? (
                  <ChevronDown className="h-3.5 w-3.5" strokeWidth={2.5} />
                ) : (
                  <ChevronUp className="h-3.5 w-3.5" strokeWidth={2.5} />
                )}
              </span>
            </span>

            {sheetExpanded && (
              <span className="mt-2 block text-[10px] font-semibold text-[#720e1e]">
                {areaLabel}
              </span>
            )}
          </button>

          {sheetExpanded && (
            <div
              id="uniair-location-picker-controls"
              className="border-t border-gray-100 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3 overscroll-contain"
              style={{ height: `${controlHeight}px`, overflowY: 'auto' }}
            >
              <div
                ref={searchSurfaceRef}
                className={searchMode ? 'sticky top-0 z-10 bg-white pb-3' : ''}
              >
                {googleSearchStatus !== 'fallback' ? (
                  <div>
                    <div className="relative rounded-2xl border border-[#720e1e]/15 bg-white p-2 shadow-sm">
                      {googleSearchStatus === 'ready' ? (
                        React.createElement('gmp-basic-place-autocomplete', {
                          ref: googleAutocompleteElementRef,
                          style: {
                            width: '100%',
                            display: 'block',
                            minHeight: '48px',
                            colorScheme: 'light',
                          },
                        })
                      ) : (
                        <div className="flex min-h-12 items-center gap-3 rounded-xl bg-white px-3 text-sm font-semibold text-gray-500">
                          <LoaderCircle className="h-5 w-5 animate-spin text-[#720e1e]" />
                          Loading Google place search…
                        </div>
                      )}
                    </div>

                    <div className="mt-2 flex items-center justify-between gap-2 px-1">
                      <p className="text-[10px] font-semibold text-gray-500">
                        Search businesses, residences, buildings, addresses and landmarks.
                      </p>
                      <span className="shrink-0 rounded-full bg-blue-50 px-2 py-1 text-[9px] font-bold uppercase tracking-[0.06em] text-blue-700">
                        Google Places
                      </span>
                    </div>
                  </div>
                ) : (
                  <div>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#720e1e]" />
                  <input
                    ref={inputRef}
                    value={query}
                    onChange={(event) => {
                      setQuery(event.target.value);
                      setError('');
                      setStatusMessage('');
                    }}
                    onFocus={() => {
                      setSheetExpanded(true);
                      setSearchMode(true);
                      const text = String(query || '').trim();
                      if (
                        suggestions.length ||
                        (text.length >= 3 && text !== String(draft?.label || '').trim())
                      ) {
                        setSuggestionsOpen(true);
                      }
                    }}
                    disabled={areaMode === 'market' && contextLoading}
                    placeholder={
                      areaMode === 'market' && contextLoading
                        ? 'Loading your booking area...'
                        : searchHint
                    }
                    className="min-h-12 w-full rounded-xl border border-gray-300 bg-white py-3 pl-11 pr-20 text-base font-medium text-gray-900 outline-none transition focus:border-[#720e1e] focus:ring-2 focus:ring-[#720e1e]/15 disabled:cursor-not-allowed disabled:bg-gray-100"
                    autoComplete="off"
                    inputMode="search"
                  />

                  {query && (
                    <button
                      type="button"
                      onClick={clearSearch}
                      className="absolute right-2 top-1/2 inline-flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100"
                      aria-label="Clear location search"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  )}

                  {searching && (
                    <LoaderCircle className="pointer-events-none absolute right-12 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-[#720e1e]" />
                  )}
                </div>

                {suggestionsOpen && (
                  <div className="mt-2 max-h-[235px] overflow-y-auto rounded-2xl border border-[#720e1e]/15 bg-white p-1 shadow-lg">
                    {suggestions.map((suggestion) => {
                      const isRetrieving = retrievingId === suggestion.providerFeatureId;

                      return (
                        <button
                          type="button"
                          key={suggestion.providerFeatureId}
                          onClick={() => chooseSuggestion(suggestion)}
                          disabled={!!retrievingId}
                          className="flex min-h-12 w-full items-start gap-3 rounded-xl px-3 py-3 text-left transition hover:bg-[#720e1e]/5 disabled:cursor-wait disabled:opacity-65"
                        >
                          <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#720e1e]/10 text-[#720e1e]">
                            {isRetrieving ? (
                              <LoaderCircle className="h-4 w-4 animate-spin" />
                            ) : (
                              <MapPin className="h-4 w-4" strokeWidth={2.2} />
                            )}
                          </span>

                          <span className="min-w-0 flex-1">
                            <span className="block break-words text-sm font-bold leading-5 text-gray-900">
                              {suggestion.label}
                            </span>
                            {suggestion.formattedAddress && (
                              <span className="mt-0.5 block break-words text-xs leading-5 text-gray-600">
                                {suggestion.formattedAddress}
                              </span>
                            )}
                            <span className="mt-1 inline-flex rounded-full bg-[#f4de9a]/65 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.06em] text-[#720e1e]">
                              {readableType(suggestion)}
                            </span>
                          </span>
                        </button>
                      );
                    })}

                    {!searching &&
                      suggestions.length === 0 &&
                      String(query || '').trim().length >= 3 &&
                      !error && (
                        <div className="p-3 text-sm leading-5 text-gray-600">
                          No matching location appeared. Check the wording, or drag the map to set the pin.
                        </div>
                      )}
                  </div>
                )}

                  </div>
                )}

                {searchMode && (
                  <div className="mt-2 rounded-xl border border-blue-100 bg-blue-50 px-3 py-2 text-[11px] font-semibold leading-5 text-blue-900">
                    Start typing, then tap the result below. The search area expands automatically so you do not have to scroll far to choose a place.
                  </div>
                )}

                {googleSearchMessage && (
                  <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] font-semibold leading-5 text-amber-900">
                    {googleSearchMessage}
                  </div>
                )}
              </div>

              <div
                className={
                  googleSearchStatus === 'ready' &&
                  (retrievingId || draft?.provider === 'google')
                    ? 'mt-3 overflow-hidden rounded-xl border border-blue-100 bg-white p-1 shadow-sm'
                    : ''
                }
                style={
                  googleSearchStatus === 'ready' &&
                  (retrievingId || draft?.provider === 'google')
                    ? undefined
                    : {
                        position: 'fixed',
                        left: '-10000px',
                        top: '0',
                        width: '320px',
                        pointerEvents: 'none',
                      }
                }
                aria-hidden={
                  !(
                    googleSearchStatus === 'ready' &&
                    (retrievingId || draft?.provider === 'google')
                  )
                }
              >
                {googleSearchStatus === 'ready' &&
                  React.createElement(
                    'gmp-place-details-compact',
                    {
                      ref: googleDetailsElementRef,
                      orientation: 'horizontal',
                      style: {
                        width: '100%',
                        display: 'block',
                        border: 'none',
                        margin: 0,
                        padding: 0,
                        backgroundColor: 'transparent',
                        colorScheme: 'light',
                      },
                    },
                    React.createElement('gmp-place-details-place-request', {
                      ref: googleDetailsRequestRef,
                    }),
                    React.createElement('gmp-place-standard-content'),
                  )}
              </div>

              <button
                type="button"
                onClick={useCurrentLocation}
                disabled={locating || (areaMode === 'market' && contextLoading)}
                className="mt-3 inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm font-bold text-gray-800 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {locating ? (
                  <LoaderCircle className="h-5 w-5 animate-spin text-[#720e1e]" />
                ) : (
                  <LocateFixed className="h-5 w-5 text-[#720e1e]" strokeWidth={2.2} />
                )}
                {locating ? 'Getting your location...' : 'Use my current location'}
              </button>

              {draft && draft.provider !== 'google' && (
                <div className="mt-3 rounded-xl border border-[#720e1e]/15 bg-[#720e1e]/5 p-3">
                  <div className="flex items-start gap-2">
                    <CheckCircle2
                      className="mt-0.5 h-5 w-5 shrink-0 text-[#720e1e]"
                      strokeWidth={2.3}
                    />
                    <div className="min-w-0">
                      <p className="text-[10px] font-bold uppercase tracking-[0.10em] text-[#720e1e]">
                        {draft.isSpecific ? 'Location selected' : 'More detail needed'}
                      </p>
                      <p className="mt-1 break-words text-sm font-bold text-gray-900">
                        {reverseLoading ? 'Looking up this exact point...' : draft.label}
                      </p>
                      {draft.formattedAddress && (
                        <p className="mt-1 break-words text-xs leading-5 text-gray-600">
                          {draft.formattedAddress}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {statusMessage && (
                <div className="mt-3 rounded-xl border border-[#d6a93b]/45 bg-[#fdf7e6] p-3 text-xs font-semibold leading-5 text-[#5a4209]">
                  {statusMessage}
                </div>
              )}

              {mapError && (
                <div className="mt-3 flex gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs font-semibold leading-5 text-amber-900">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  {mapError}
                </div>
              )}

              {error && (
                <div className="mt-3 flex gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-xs font-semibold leading-5 text-red-700">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  {error}
                </div>
              )}

              <button
                type="button"
                onClick={confirm}
                disabled={!canConfirm}
                className={`mt-4 min-h-12 w-full rounded-xl px-4 py-3 text-sm font-bold transition ${
                  canConfirm
                    ? 'bg-[#720e1e] text-white shadow-lg shadow-[#720e1e]/20 hover:bg-[#470611]'
                    : 'cursor-not-allowed bg-gray-200 text-gray-500'
                }`}
              >
                Use this location
              </button>

              {onClear && value?.label && (
                <button
                  type="button"
                  onClick={onClear}
                  className="mt-3 min-h-11 w-full text-center text-xs font-bold text-[#720e1e] underline underline-offset-4"
                >
                  Clear selected location
                </button>
              )}

              <p className="mt-3 text-center text-[10px] leading-4 text-gray-400">
                {googleSearchStatus === 'ready'
                  ? 'Place search powered by Google · Pin address lookup by Geoapify · Map data © OpenStreetMap contributors'
                  : 'Location search powered by Geoapify · Map data © OpenStreetMap contributors'}
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );

  return createPortal(picker, document.body);
}